import { useEffect } from "react";
import { motion, useMotionValue, useTransform, useReducedMotion, animate } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

interface StatCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ElementType;
  /** Pass "duotone" | "fill" | "bold" for Phosphor icons. Lucide icons ignore this prop. */
  iconWeight?: string;
  trend?: { value: string; positive: boolean };
  variant?: "default" | "success" | "warning" | "destructive" | "info";
  delay?: number;
}

function AnimatedNumber({ value, suffix = "" }: { value: number; suffix?: string }) {
  const reduced = useReducedMotion();
  const motionVal = useMotionValue(0);
  const display = useTransform(motionVal, (v) => {
    if (suffix === "%") return `${v.toFixed(1)}%`;
    return Math.round(v).toLocaleString();
  });

  useEffect(() => {
    if (reduced) {
      motionVal.set(value);
      return;
    }
    const controls = animate(motionVal, value, {
      duration: 1.2,
      ease: [0.32, 0.72, 0, 1],
    });
    return controls.stop;
  }, [motionVal, value, reduced]);

  return <motion.span>{display}</motion.span>;
}

function AnimatedValue({ value }: { value: string | number }) {
  if (typeof value === "number") return <AnimatedNumber value={value} />;
  const pctMatch = typeof value === "string" ? value.match(/^(\d+\.?\d*)%$/) : null;
  if (pctMatch) return <AnimatedNumber value={parseFloat(pctMatch[1])} suffix="%" />;
  return <span>{value}</span>;
}

const variantClasses: Record<string, string> = {
  default:     "bg-primary/10 text-primary",
  success:     "bg-green-500/10 text-green-600",
  warning:     "bg-amber-500/10 text-amber-600",
  destructive: "bg-destructive/10 text-destructive",
  info:        "bg-blue-500/10 text-blue-600",
};

export function StatCard({
  title, value, subtitle, icon: Icon, iconWeight, trend, variant = "default", delay = 0,
}: StatCardProps) {
  const reduced = useReducedMotion();
  // Cast to any so Phosphor's `weight` prop doesn't cause TS errors on Lucide icons
  const AnyIcon = Icon as any;
  return (
    <motion.div
      initial={{ opacity: 0, y: reduced ? 0 : 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: reduced ? 0 : 0.35, delay: reduced ? 0 : delay, ease: [0.4, 0, 0.2, 1] }}
    >
      <Card>
        <CardContent className="flex items-start gap-4 p-4">
          <div className={cn("flex h-10 w-10 shrink-0 items-center justify-center rounded-lg", variantClasses[variant])}>
            <AnyIcon className="h-5 w-5" weight={iconWeight} />
          </div>
          <div className="flex flex-col min-w-0 overflow-hidden">
            <span className="text-xs font-medium text-muted-foreground truncate">{title}</span>
            <span className="text-xl font-bold tracking-tight leading-tight break-all">
              <AnimatedValue value={value} />
            </span>
            {subtitle && <span className="text-xs text-muted-foreground truncate">{subtitle}</span>}
            {trend && (
              <span className={cn("text-xs font-medium truncate", trend.positive ? "text-green-600" : "text-destructive")}>
                {trend.positive ? "↑" : "↓"} {trend.value}
              </span>
            )}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}
